import { iconFor as iconForImpl, createDragPreviewElement as createDragPreviewElementImpl } from '../game/assets/icons.js';
import { comboMap as comboMapImpl, combinePair as combinePairImpl, recipesByResult as recipesByResultImpl } from '../game/core/combiner.js';
import { eraIndexByKey as eraIndexByKeyImpl } from '../game/core/era.js';
import { CONSTANTS } from '../data.js';
import type { CardInstance } from '../types';

// Compatibility layer that re-exports utilities from their new modular locations.
// This keeps existing imports working while improving code organization.

/**
 * Resolve the icon URL for a given element key using the asset manifest.
 */
export const iconFor = iconForImpl;

/**
 * Create a custom drag preview element mimicking board tiles.
 */
export const createDragPreviewElement = createDragPreviewElementImpl;

/**
 * Memoized map of combinations for quick lookups.
 */
export const comboMap = comboMapImpl;

/**
 * Given two element keys, returns the resulting element key if a valid
 * combination exists, otherwise undefined.
 */
export const combinePair = combinePairImpl;

/**
 * Memoized map of recipes, keyed by the result element.
 */
export const recipesByResult = recipesByResultImpl;

/**
 * Memoized map of era keys to their index for sorting/comparison.
 */
export const eraIndexByKey = eraIndexByKeyImpl;

/**
 * Calculates the stats of a card, including any bonuses.
 * If the card has component cards, sums their stats instead of using base category stats.
 */
export const getCardStats = (card: CardInstance, currentBattleEraKey: string) => {
  const element = CONSTANTS.elements[card.elementKey];
  if (!element) return { power: 1, description: "An unknown entity." };
  
  let power = 0;
  let description = "";

  // If this card was created from combining other cards, sum their stats
  if (card.componentCards && card.componentCards.length > 0) {
    power = card.componentCards.reduce((sum, componentCard) => {
      return sum + getCardStats(componentCard, currentBattleEraKey).power;
    }, 0);
    description = `Combined power from ${card.componentCards.length} cards.`;
  } else {
    // Use base category stats for non-combined cards
    const category = element.category || 'materials';
    const stats = CONSTANTS.cardGame.categoryStats[category];
    if (!stats) return { power: 1, description: "A mysterious power." };
    
    const elementEraIndex = eraIndexByKey[element.era] ?? 0;
    const eraMultiplier = 1 + (elementEraIndex * (CONSTANTS.cardGame.eraPowerMultiplier || 0));
    power = Math.round(stats.power * eraMultiplier);
    description = stats.description;
  }

  // Apply future time bonus
  if (card.isFuture) {
    const cardEraIndex = eraIndexByKey[element.era] ?? 0;
    const battleEraIndex = eraIndexByKey[currentBattleEraKey] ?? 0;
    const eraDiff = Math.max(0, cardEraIndex - battleEraIndex);
    power += eraDiff * (CONSTANTS.cardGame.futureTimeBonusPerEra || 0);
  }

  return {
      power,
      description
  };
};
